// ═══════════════════════════════════════════════════════════════════
//  syncLock — guarantees the web client is always talking to the same
//  backend (and therefore the same database) as the mobile app.
//
//  How it works
//    1. On boot we hit /api/sync/identity and cache `installId` in
//       localStorage. This is a UUID generated once per database.
//    2. Every 30 s we re-poll. If the server returns a DIFFERENT
//       installId, the user is now pointed at a totally different
//       backend → we lock the UI behind a full-screen warning until
//       the user resets (or the situation is restored).
//    3. We also track `lastChangeAt`. If it advances, listeners get
//       notified — UI components can then refresh their data.
//
//  Public API
//    initSyncLock(API_BASE)         → starts the poll loop; resolves
//                                     to the current identity object
//    onSyncChange(fn)               → subscribe to "data changed" events
//    onSyncMismatch(fn)             → subscribe to "wrong server" events
//    getStoredInstallId()           → cached id (or null)
//    resetStoredInstallId()         → forget the cached id (after a
//                                     deliberate server change)
// ═══════════════════════════════════════════════════════════════════

import axios from 'axios';

const KEY = 'oralzy_sync_install_id';

const changeListeners   = new Set();
const mismatchListeners = new Set();

let _baseUrl  = '';
let _interval = null;
let _lastSeenChangeAt = 0;
let _identity = null;

export const onSyncChange   = (fn) => { changeListeners.add(fn);   return () => changeListeners.delete(fn); };
export const onSyncMismatch = (fn) => { mismatchListeners.add(fn); return () => mismatchListeners.delete(fn); };

export const getStoredInstallId  = () => { try { return localStorage.getItem(KEY); } catch { return null; } };
export const resetStoredInstallId = () => { try { localStorage.removeItem(KEY); } catch {} };

async function check() {
  try {
    const r = await axios.get(`${_baseUrl}/sync/identity`, { timeout: 8000 });
    const id = r.data;
    _identity = id;
    const stored = getStoredInstallId();
    if (!stored) {
      // First time we see this server — remember it.
      try { localStorage.setItem(KEY, id.installId); } catch {}
    } else if (stored !== id.installId) {
      // Different backend!! Lock the UI.
      mismatchListeners.forEach(fn => { try { fn({ stored, current: id.installId, info: id }); } catch {} });
      return id;
    }
    const seen = new Date(id.lastChangeAt).getTime() || 0;
    if (_lastSeenChangeAt && seen > _lastSeenChangeAt) {
      changeListeners.forEach(fn => { try { fn({ lastChangeAt: id.lastChangeAt, counters: id.changeCounters || {} }); } catch {} });
    }
    _lastSeenChangeAt = seen;
    return id;
  } catch (e) {
    // Soft-fail — server unreachable just means no sync update this tick.
    return null;
  }
}

export async function initSyncLock(API_BASE) {
  _baseUrl = API_BASE;
  // Run an immediate check + then every 30 s.
  const first = await check();
  if (_interval) clearInterval(_interval);
  _interval = setInterval(check, 30 * 1000);
  return first;
}

export function getLastIdentity() { return _identity; }
